import { useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  emptyVehiculo,
  initialAfiliacionForm,
  WIZARD_STEPS,
  type AfiliacionForm,
  type DocumentoForm,
  type VehiculoForm,
} from "./types";

export type SubmitStatus = "idle" | "loading" | "success" | "error";

type FieldErrors = Record<string, string>;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateStep(step: number, form: AfiliacionForm): FieldErrors {
  const errors: FieldErrors = {};
  const stepId = WIZARD_STEPS[step].id;

  if (stepId === "tipo") {
    if (!form.tipo) errors.tipo = "Selecciona cómo quieres formar parte.";
  }

  if (stepId === "datos") {
    if (form.tipo === "independiente") {
      if (!form.nombreCompleto.trim()) errors.nombreCompleto = "Ingresa tu nombre completo.";
      if (!form.telefono.trim()) errors.telefono = "Ingresa un teléfono de contacto.";
      if (!form.email.trim()) errors.email = "Ingresa tu correo electrónico.";
      if (!form.ciudad.trim()) errors.ciudad = "Indica tu ciudad.";
      if (!form.provincia.trim()) errors.provincia = "Indica tu provincia.";
      if (!form.rolVehiculo) errors.rolVehiculo = "Indica cómo participas.";
    } else if (form.tipo === "empresa") {
      if (!form.empresaNombreComercial.trim() && !form.empresaRazonSocial.trim()) {
        errors.empresaNombreComercial = "Ingresa el nombre comercial o la razón social.";
      }
      if (!form.contactoNombre.trim()) errors.contactoNombre = "Ingresa la persona de contacto.";
      if (!form.telefono.trim()) errors.telefono = "Ingresa un teléfono de contacto.";
      if (!form.email.trim()) errors.email = "Ingresa el correo de la empresa.";
      if (!form.ciudad.trim()) errors.ciudad = "Indica la ciudad de la empresa.";
      if (!form.provincia.trim()) errors.provincia = "Indica la provincia.";
    }
    if (form.email.trim() && !emailRegex.test(form.email.trim())) {
      errors.email = "Ingresa un correo electrónico válido.";
    }
  }

  if (stepId === "vehiculos") {
    if (form.vehiculos.length === 0) {
      errors.vehiculos = "Agrega al menos un vehículo.";
    }
    form.vehiculos.forEach((veh, index) => {
      if (!veh.tipo) errors[`veh-${index}-tipo`] = "Selecciona el tipo de vehículo.";
      if (veh.tipo === "Otro" && !veh.tipoPersonalizado.trim()) {
        errors[`veh-${index}-tipoPersonalizado`] = "Especifica el tipo de vehículo.";
      }
      if (!veh.marca.trim()) errors[`veh-${index}-marca`] = "Ingresa la marca.";
      if (!veh.modelo.trim()) errors[`veh-${index}-modelo`] = "Ingresa el modelo.";
      if (!veh.placa.trim()) errors[`veh-${index}-placa`] = "Ingresa la placa.";
    });
  }

  if (stepId === "servicios") {
    if (form.servicios.length === 0) {
      errors.servicios = "Selecciona al menos un servicio que puedas ofrecer.";
    }
  }

  return errors;
}

export function useAfiliacionForm() {
  const [form, setForm] = useState<AfiliacionForm>(initialAfiliacionForm);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState("");
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [numero, setNumero] = useState("");

  const setField = <K extends keyof AfiliacionForm>(name: K, value: AfiliacionForm[K]) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addVehiculo = () =>
    setForm((prev) => ({ ...prev, vehiculos: [...prev.vehiculos, emptyVehiculo()] }));

  const removeVehiculo = (id: string) =>
    setForm((prev) => ({ ...prev, vehiculos: prev.vehiculos.filter((veh) => veh.id !== id) }));

  const updateVehiculo = (id: string, patch: Partial<VehiculoForm>) =>
    setForm((prev) => ({
      ...prev,
      vehiculos: prev.vehiculos.map((veh) => (veh.id === id ? { ...veh, ...patch } : veh)),
    }));

  const toggleServicio = (servicio: string) =>
    setForm((prev) => ({
      ...prev,
      servicios: prev.servicios.includes(servicio)
        ? prev.servicios.filter((item) => item !== servicio)
        : [...prev.servicios, servicio],
    }));

  const setDocumentosGenerales = (documentos: DocumentoForm[]) =>
    setForm((prev) => ({ ...prev, documentosGenerales: documentos }));

  const next = () => {
    const stepErrors = validateStep(step, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      setFormError("Revisa los campos marcados para continuar.");
      return;
    }
    setErrors({});
    setFormError("");
    setStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const prev = () => {
    setErrors({});
    setFormError("");
    setStep((s) => Math.max(s - 1, 0));
  };

  const goTo = (target: number) => {
    if (target <= step) {
      setErrors({});
      setFormError("");
      setStep(target);
    }
  };

  const buildPayload = () => ({
    tipo: form.tipo,
    nombre_completo: form.nombreCompleto.trim(),
    cedula: form.cedula.trim(),
    telefono: form.telefono.trim(),
    whatsapp: form.whatsapp.trim(),
    email: form.email.trim(),
    ciudad: form.ciudad.trim(),
    provincia: form.provincia.trim(),
    direccion_zona: form.direccionZona.trim(),
    rol_vehiculo: form.rolVehiculo,
    empresa_nombre_comercial: form.empresaNombreComercial.trim(),
    empresa_razon_social: form.empresaRazonSocial.trim(),
    rnc: form.rnc.trim(),
    contacto_nombre: form.contactoNombre.trim(),
    contacto_cargo: form.contactoCargo.trim(),
    sitio_web: form.sitioWeb.trim(),
    redes: form.redes.trim(),
    servicios: form.servicios,
    disponibilidad_general: {
      dias: form.dispDias,
      horario: form.dispHorario,
      zona: form.dispZona,
      distancia_maxima: form.dispDistancia,
    },
    vehiculos: form.vehiculos.map((veh, index) => ({
      tipo: veh.tipo,
      tipo_personalizado: veh.tipoPersonalizado.trim(),
      marca: veh.marca.trim(),
      modelo: veh.modelo.trim(),
      anio: veh.anio.trim(),
      color: veh.color.trim(),
      placa: veh.placa.trim(),
      capacidad: veh.capacidad.trim(),
      tipo_carga: veh.tipoCarga,
      caracteristicas: veh.caracteristicas
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      zona_operacion: veh.zonaOperacion.trim(),
      disponibilidad: veh.disponibilidad,
      dias_disponibles: veh.diasDisponibles,
      horario: veh.horario.trim(),
      distancia_maxima: veh.distanciaMaxima.trim(),
      foto_url: veh.fotoUrl,
      orden: index,
      documentos: veh.documentos.map((doc) => ({
        tipo: doc.tipo,
        archivo_url: doc.archivoUrl,
        archivo_nombre: doc.archivoNombre,
      })),
    })),
    documentos_generales: form.documentosGenerales.map((doc) => ({
      tipo: doc.tipo,
      archivo_url: doc.archivoUrl,
      archivo_nombre: doc.archivoNombre,
    })),
  });

  const reset = () => {
    setForm(initialAfiliacionForm);
    setStep(0);
    setErrors({});
    setFormError("");
    setStatus("idle");
    setNumero("");
  };

  const handleSubmit = async () => {
    const allStepErrors: FieldErrors = {};
    for (let i = 0; i < WIZARD_STEPS.length; i += 1) {
      Object.assign(allStepErrors, validateStep(i, form));
    }
    if (Object.keys(allStepErrors).length > 0) {
      setErrors(allStepErrors);
      setFormError("Falta completar información antes de enviar la solicitud.");
      setStatus("error");
      return;
    }

    const payload = buildPayload();
    setErrors({});
    setFormError("");
    setStatus("loading");

    try {
      const { data, error } = await supabase.rpc("crear_solicitud_afiliacion", { payload });
      if (error) throw error;
      const result = data as { numero?: string; id?: string } | null;
      const numeroGenerado = result?.numero ?? "";
      setNumero(numeroGenerado);
      setStatus("success");

      void supabase.functions
        .invoke("notificar-afiliacion", {
          body: { afiliacion: { ...payload, numero: numeroGenerado, id: result?.id } },
        })
        .then(({ error: fnError }) => {
          if (fnError) console.warn("No se pudo notificar la afiliación:", fnError.message);
        });
    } catch {
      setStatus("error");
      setFormError(
        "No pudimos registrar tu solicitud. Revisa tu conexión e intenta nuevamente, o contáctanos por teléfono.",
      );
    }
  };

  return {
    form,
    setField,
    step,
    errors,
    formError,
    status,
    numero,
    next,
    prev,
    goTo,
    addVehiculo,
    removeVehiculo,
    updateVehiculo,
    toggleServicio,
    setDocumentosGenerales,
    handleSubmit,
    reset,
  };
}